// The product-type browser: what a product is, picked from the product-type tree (API catalog
// lib/product-types) — the groups at the top as a grid (Food, Clothes, Tickets…), a group opened to its
// categories and types, a breadcrumb back, and a search over every type's names and keywords in the language
// switched to (Bulgarian first). A type that is not listed is named by the vendor ("Your product type is not
// listed?") and proposed under the group they are in: the admin approves it with the product's review.
// Vendors and admins alike: the New product dialog and the product's Tags tab.
import React, { useEffect, useMemo, useState } from "react"
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  CheckIcon,
  BookOpenIcon,
  MusicalNoteIcon,
  CakeIcon,
  ShoppingBagIcon,
  TagIcon,
  BriefcaseIcon,
  SparklesIcon,
  ComputerDesktopIcon,
  HomeIcon,
  WrenchScrewdriverIcon,
  PuzzlePieceIcon,
  TrophyIcon,
  TruckIcon,
  HeartIcon,
  PencilSquareIcon,
  CloudArrowDownIcon,
  TicketIcon,
  GiftIcon,
  PaintBrushIcon,
} from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import { TextInput } from "../ui"
import { useProductTypes, nameOf, searchTypes, LANGUAGES } from "./useProductTypes"

const card = "flex items-center gap-3 rounded-lg border border-admin_bordered bg-admin_paper px-3 py-2.5 text-left hover:border-admin_primary hover:bg-admin_primary/5 transition-colors"

// the groups' icons, by the heroicon name a group names (scripts/seed/product-types.json in the API)
const ICONS = { BookOpenIcon, MusicalNoteIcon, CakeIcon, ShoppingBagIcon, TagIcon, BriefcaseIcon, SparklesIcon, ComputerDesktopIcon, HomeIcon, WrenchScrewdriverIcon, PuzzlePieceIcon, TrophyIcon, TruckIcon, HeartIcon, PencilSquareIcon, CloudArrowDownIcon, TicketIcon, GiftIcon, PaintBrushIcon }

/** A group's icon, else a tag. */
const IconOf = ({ name, className }) => {
  const Icon = (name && ICONS[name]) || TagIcon
  return <Icon className={className} />
}

/**
 * `value`: the picked type's slug; `proposal`: a type named instead ({ name, language, parentSlug });
 * `onChange({ slug, node })` a type picked; `onPropose({ name, language, parentSlug })` one named;
 * `t`: the host's texts.
 */
export default function ProductTypeBrowser({ value = "", proposal = null, onChange, onPropose, t = (s) => s, language: initialLanguage = "bg", className = "" }) {
  const { list, bySlug, childrenOf, tops, loading } = useProductTypes()
  const [language, setLanguage] = useState(initialLanguage)
  const [query, setQuery] = useState("")
  // the group being looked into (null: the top)
  const [current, setCurrent] = useState(null)
  const [naming, setNaming] = useState(false)
  const [newName, setNewName] = useState("")

  // a picked type opens at its group
  useEffect(() => {
    if (!value || current !== null || !bySlug.size) return
    const node = bySlug.get(value)
    if (node?.parents?.[0]) setCurrent(node.parents[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, bySlug.size])

  const name = (node) => nameOf(node, language)
  const trail = current ? bySlug.get(current)?.path || [] : []
  const results = useMemo(() => (query.trim() ? searchTypes(list, query, language) : []), [list, query, language])
  // what a name being proposed is most like: offered before a duplicate is made
  const lookalikes = useMemo(() => (newName.trim().length >= 3 ? searchTypes(list, newName, language).slice(0, 5) : []), [list, newName, language])

  const pick = (node) => {
    if (node.childCount) {
      setCurrent(node.slug)
      setQuery("")
      return
    }
    setNaming(false)
    onChange?.({ slug: node.slug, node })
  }
  const pathText = (node) => node.path.slice(0, -1).map((slug) => name(bySlug.get(slug))).filter(Boolean).join(" › ")

  const propose = () => {
    const text = newName.trim()
    if (text.length < 2) return
    onPropose?.({ name: text, language, parentSlug: current || "" })
    setNaming(false)
  }

  const kids = current ? (childrenOf.get(current) || []).map((slug) => bySlug.get(slug)).filter(Boolean) : tops
  const groups = kids.filter((node) => node.childCount)
  const leaves = kids.filter((node) => !node.childCount)

  return (
    <div className={`flex flex-col gap-3 ${className}`} data-testid="product-type-browser">
      {/* the search, and the language it reads */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-admin_text_secondary pointer-events-none" />
          <TextInput value={query} onChange={setQuery} placeholder={t`Search product types…`} className="pl-9" data-testid="product-type-search" />
        </div>
        <div className="inline-flex rounded-full border border-admin_bordered p-0.5" role="radiogroup" aria-label={t`Language`}>
          {LANGUAGES.map((code) => (
            <button
              key={code}
              type="button"
              role="radio"
              aria-checked={language === code}
              onClick={() => setLanguage(code)}
              className={`px-2.5 h-7 rounded-full text-xs font-semibold uppercase ${language === code ? "bg-admin_primary text-white" : "text-admin_text_secondary hover:text-admin_text"}`}
              data-testid={`product-type-lang-${code}`}
            >
              {code}
            </button>
          ))}
        </div>
      </div>

      {/* where in the tree */}
      {!query.trim() && (
        <nav className="flex flex-wrap items-center gap-1 text-sm" aria-label={t`Product type`} data-testid="product-type-breadcrumb">
          <button type="button" onClick={() => setCurrent(null)} className={`hover:text-admin_primary ${current ? "text-admin_text_secondary" : "font-semibold text-admin_text"}`}>
            {t`All types`}
          </button>
          {trail.map((slug, index) => (
            <React.Fragment key={slug}>
              <ChevronRightIcon className="w-3.5 h-3.5 text-admin_text_secondary" />
              <button type="button" onClick={() => setCurrent(slug)} className={`hover:text-admin_primary ${index === trail.length - 1 ? "font-semibold text-admin_text" : "text-admin_text_secondary"}`}>
                {name(bySlug.get(slug))}
              </button>
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className="max-h-[340px] overflow-y-auto pr-1">
        {loading && <div className="py-6 text-center text-sm text-admin_text_secondary">{t`Loading…`}</div>}

        {/* the search's answers: a type with the way to it */}
        {!loading && query.trim() && (
          <div className="flex flex-col gap-1" data-testid="product-type-results">
            {!results.length && <div className="py-4 text-center text-sm text-admin_text_secondary">{t`No type matches. Name yours below.`}</div>}
            {results.map((node) => (
              <button key={node.slug} type="button" onClick={() => pick(node)} className={`${card} ${value === node.slug ? "border-admin_primary bg-admin_primary/5" : ""}`} data-testid="product-type-result" data-slug={node.slug}>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold truncate">{name(node)}</span>
                  <span className="block text-xs text-admin_text_secondary truncate">{pathText(node)}</span>
                </span>
                {node.childCount ? <ChevronRightIcon className="w-4 h-4 text-admin_text_secondary" /> : value === node.slug && <CheckIcon className="w-4 h-4 text-admin_primary" />}
              </button>
            ))}
          </div>
        )}

        {/* the tree: groups as a grid, types as a list */}
        {!loading && !query.trim() && (
          <div className="flex flex-col gap-3">
            {!!groups.length && (
              <div className={`grid gap-2 ${current ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`} data-testid="product-type-groups">
                {groups.map((node) => (
                  <button key={node.slug} type="button" onClick={() => pick(node)} className={card} data-testid="product-type-group" data-slug={node.slug}>
                    {!current && (
                      <span className="w-9 h-9 shrink-0 rounded-lg bg-admin_primary/10 text-admin_primary flex items-center justify-center">
                        <IconOf name={node.icon} className="w-5 h-5" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1 text-sm font-semibold leading-tight">{name(node)}</span>
                    <ChevronRightIcon className="w-4 h-4 shrink-0 text-admin_text_secondary" />
                  </button>
                ))}
              </div>
            )}
            {!!leaves.length && (
              <div className="flex flex-wrap gap-2" data-testid="product-type-leaves">
                {leaves.map((node) => (
                  <button
                    key={node.slug}
                    type="button"
                    onClick={() => pick(node)}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-sm transition-colors ${value === node.slug ? "border-admin_primary bg-admin_primary text-white" : "border-admin_bordered hover:border-admin_primary"} ${node.status !== "published" ? "border-dashed" : ""}`}
                    data-testid="product-type-leaf"
                    data-slug={node.slug}
                  >
                    {value === node.slug && <CheckIcon className="w-3.5 h-3.5" />}
                    {name(node)}
                    {node.status !== "published" && <span className="text-[10px] uppercase opacity-70">{t`proposed`}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* not listed: named, under the group open now */}
      {!naming ? (
        <button type="button" onClick={() => setNaming(true)} className="self-start inline-flex items-center gap-1 text-sm font-semibold text-admin_primary hover:underline" data-testid="product-type-not-listed">
          <PlusIcon className="w-4 h-4" />
          {t`Your product type is not listed?`}
        </button>
      ) : (
        <div className="flex flex-col gap-2 rounded-md border border-dashed border-admin_bordered p-3" data-testid="product-type-propose">
          {/* named at once, wherever the browser is: under the group open, else our team places it */}
          <>
              <span className="text-xs text-admin_text_secondary">
                {current ? t("It will be added under %s and approved with the product.", trail.map((slug) => name(bySlug.get(slug))).join(" › ")) : t`Our team places it in the right group when they review the product.`}
              </span>
              <div className="flex gap-2">
                <TextInput value={newName} onChange={setNewName} placeholder={t`Your product type`} maxLength={80} className="flex-1" autoFocus data-testid="product-type-new-name" />
                <Button type="button" variant="admin_primary" onClick={propose} disabled={newName.trim().length < 2} data-testid="product-type-propose-use">
                  {t`Use this type`}
                </Button>
              </div>
              {/* a type that is there under this name already: picked instead of a duplicate */}
              {!!lookalikes.length && (
                <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="product-type-lookalikes">
                  <span className="text-admin_text_secondary">{t`Is it one of these?`}</span>
                  {lookalikes.map((node) => (
                    <button key={node.slug} type="button" onClick={() => pick(node)} className="px-2 h-6 rounded-full border border-admin_bordered hover:border-admin_primary" title={pathText(node)}>
                      {name(node)}
                    </button>
                  ))}
                </div>
              )}
          </>
          <button type="button" onClick={() => setNaming(false)} className="self-end text-xs text-admin_text_secondary hover:text-admin_text">
            {t`Cancel`}
          </button>
        </div>
      )}
    </div>
  )
}
